import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useRef } from 'react';
import type { BookLanguageRow } from '../../Settings/lists/singleTableListSupport';
import {
  BOOK_COVER_PROMO_IMAGE_HEIGHT_PX,
  BOOK_COVER_PROMO_IMAGE_WIDTH_PX,
  BOOK_IMAGE_ACCEPT,
} from './constants';
import CoverImageAppAspectPreview from './CoverImageAppAspectPreview';
import type { BookFormDraft } from './types';

export type BookFormStepImagesProps = {
  draft: BookFormDraft;
  onChange: (next: BookFormDraft) => void;
  enabledLanguages: BookLanguageRow[];
  stepNavigation: React.ReactNode;
};

export default function BookFormStepImages({
  draft,
  onChange,
  enabledLanguages,
  stepNavigation,
}: BookFormStepImagesProps) {
  const coverInputRef = useRef<HTMLInputElement>(null);
  const promoInputRef = useRef<HTMLInputElement>(null);

  const coverPromoPreviewSx = {
    maxWidth: '100%',
    maxHeight: 'min(85dvh, calc(100dvw - 32px))',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain' as const,
    display: 'block',
  };

  return (
    <Box>
      {enabledLanguages.length === 0 ? (
        <Alert severity="info">Эхний алхамд дор хаяж нэг хэл идэвхжүүлнэ үү.</Alert>
      ) : (
        <>
          <Typography
            variant="h5"
            component="h2"
            sx={(theme) => ({ fontWeight: 600, color: theme.palette.text.primary })}
          >
            Зураг
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 'md' }}>
            Нүүр болон промо зургийг {BOOK_COVER_PROMO_IMAGE_WIDTH_PX}×{BOOK_COVER_PROMO_IMAGE_HEIGHT_PX} пиксел
            (өргөн {BOOK_COVER_PROMO_IMAGE_WIDTH_PX} пиксел) хэмжээтэй байлгана уу. Промо зураг заавал биш.
          </Typography>

          <Stack spacing={2} sx={{ mb: 2 }}>
            <input
              ref={coverInputRef}
              type="file"
              accept={BOOK_IMAGE_ACCEPT}
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                onChange({ ...draft, cover_image_file: f });
                e.target.value = '';
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<AddPhotoAlternateOutlined />}
                onClick={() => coverInputRef.current?.click()}
              >
                Нүүр зураг сонгох
              </Button>
              {(draft.cover_image_file || draft.cover_image_path.trim()) && (
                <Button
                  type="button"
                  size="small"
                  onClick={() => onChange({ ...draft, cover_image_file: null, cover_image_path: '' })}
                >
                  Арилгах
                </Button>
              )}
            </Stack>
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', my: 1 }}>
              <CoverImageAppAspectPreview
                file={draft.cover_image_file}
                storageKey={draft.cover_image_path}
                imageSx={coverPromoPreviewSx}
              />
            </Box>
          </Stack>

          <Stack spacing={2} sx={{ mb: 2 }}>
            <input
              ref={promoInputRef}
              type="file"
              accept={BOOK_IMAGE_ACCEPT}
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                onChange({ ...draft, promo_image_file: f });
                e.target.value = '';
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<AddPhotoAlternateOutlined />}
                onClick={() => promoInputRef.current?.click()}
              >
                Промо зураг сонгох (заавал биш)
              </Button>
              {(draft.promo_image_file || draft.promo_image_path.trim()) && (
                <Button
                  type="button"
                  size="small"
                  onClick={() => onChange({ ...draft, promo_image_file: null, promo_image_path: '' })}
                >
                  Арилгах
                </Button>
              )}
            </Stack>
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', my: 1 }}>
              <CoverImageAppAspectPreview
                file={draft.promo_image_file}
                storageKey={draft.promo_image_path}
                imageSx={coverPromoPreviewSx}
                variant="promo"
              />
            </Box>
          </Stack>
        </>
      )}
      {stepNavigation}
    </Box>
  );
}
